import React, { ForwardedRef, ReactText, useState } from 'react';
import { Grid, Box } from '@mui/material';
import {
  ChartColumn,
  SpaceDataRowContainer,
  SpaceNamesContainer,
  SpaceNameRow,
  ChartTitle,
  SmallBubbleIcon,
  SpaceCategoryName,
  ChartContainer,
  Pill,
  SmallDonutChartCard,
  ChartSummaryHeader,
  ChartSummaryHeaderRow,
  PercentColumn,
  SpaceNameListRow,
  ChartSummaryPill,
  DisclaimerTextContainer,
  ControlledLineBreaks,
} from 'spw-components/charts/styles';
import SmallDonutChart from 'spw-components/charts/small-donut-chart';
import {
  asPercentageRounded,
  asUsf,
  numberWithCommasOrDash,
} from 'spw-shared/utils';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import { JustifyCenterColumn } from 'spw-shared/globalComponents/grid';
import SpaceTypeAllocationDialog from 'spw-components/dialogs/total-program/space-type-allocation-dialog';
import { SPACE_TYPE_ALLOCATION } from 'spw-components/dialogs/total-program/utils';
import { useSelector } from 'react-redux';
import { currentProject } from 'spw-redux/modules/projects/selectors';
import { PlainWorksheet } from '../../models/Worksheet';

export interface ISpaceChartData {
  quantity: number;
  percent: number;
  color: string;
  name: string;
  category?: string;
}

interface ISmallDonutChartDetailProps {
  title: string;
  subtitle: string;
  total: number;
  spacesData: ISpaceChartData[];
  data: ReactText[][];
  colors?: string[];
  showPercentPill?: boolean;
  forceDisableAllocationDialog?: boolean;
  showNamesInTable?: boolean;
  showStackedNames?: boolean;
  includeDisclaimer?: boolean;
  style?: { [key: string]: number | string };
  isTotalProgramScenario?: boolean;
  totalProgramWorksheets?: PlainWorksheet[];
  categoryListStyle?: { [key: string]: number | string };
  spaceTypeNamesStyle?: { [key: string]: number | string };
  testingPrefix: string;
  anchorRef?: ForwardedRef<HTMLElement>;
  isDetailedChart?: boolean;
}

const SpaceRows = ({
  spacesData,
  showPercentPill,
  dialog,
  openDialog,
  testingPrefix,
}: {
  spacesData: ISpaceChartData[];
  showPercentPill: boolean;
  dialog?: boolean;
  openDialog?: (
    selectedValueType: string,
    usfValue: number | string,
    usfPercentValue: number | string
  ) => void;
  testingPrefix: string;
}) => {
  return (
    <>
      {spacesData.map((spaceChartData: ISpaceChartData, index) => {
        const usf = spaceChartData.quantity
          ? asUsf(spaceChartData.quantity)
          : NO_DATA_TO_SHOW;

        const usfPercent = asPercentageRounded(spaceChartData.percent, true);

        return (
          <SpaceDataRowContainer
            key={`row-${spaceChartData.name}-${spaceChartData.category}`}
            data-testid={`row-${spaceChartData.name}`}
            onClick={() => {
              openDialog(spaceChartData.category, usf, usfPercent);
            }}
            className={`${dialog ? 'highlight' : ''}`}
          >
            <Grid item xs={2}>
              <ChartColumn className="bubble-column">
                <SmallBubbleIcon color={spaceChartData.color} />
              </ChartColumn>
            </Grid>
            <Grid item xs={10}>
              <Box display="flex">
                <ChartColumn
                  className="right"
                  style={{ marginLeft: 'auto' }}
                  data-testid={`${testingPrefix}_row${index}_quantity`}
                >
                  {numberWithCommasOrDash(spaceChartData.quantity)}
                </ChartColumn>
                <PercentColumn className="percent right">
                  <Box data-testid={`${testingPrefix}_row${index}_percent`}>
                    {showPercentPill ? (
                      <Pill color="transparent">
                        {asPercentageRounded(spaceChartData.percent, true)}
                      </Pill>
                    ) : (
                      asPercentageRounded(spaceChartData.percent, true)
                    )}
                  </Box>
                </PercentColumn>
              </Box>
            </Grid>
          </SpaceDataRowContainer>
        );
      })}
    </>
  );
};

const SpaceTypeNames = ({
  spacesData,
  testingPrefix,
}: {
  spacesData: ISpaceChartData[];
  testingPrefix: string;
}) => {
  return (
    <SpaceNamesContainer>
      {spacesData.map((spaceData: ISpaceChartData, index: number) => (
        <SpaceNameRow
          xs={12}
          key={`${spaceData.name}-${spaceData.category}-row`}
        >
          <Grid container>
            <Grid item xs={2}>
              <ChartColumn className="bubble-column">
                <SmallBubbleIcon color={spaceData.color} />
              </ChartColumn>
            </Grid>
            <SpaceCategoryName
              data-testid={`${testingPrefix}_cat_name${index}`}
            >
              {spaceData.name}
            </SpaceCategoryName>
          </Grid>
        </SpaceNameRow>
      ))}
    </SpaceNamesContainer>
  );
};

const StackedCategoryNames = ({
  spacesData,
  testingPrefix,
}: {
  spacesData: ISpaceChartData[];
  testingPrefix: string;
}) => {
  return (
    <JustifyCenterColumn>
      {spacesData.map((space: ISpaceChartData, index: number) => (
        <SpaceNameListRow
          key={`space-${space.name}-${space.category}`}
          className={index === 0 ? 'first' : ''}
        >
          <Grid item xs={12}>
            <Box display="flex">
              <Box display="flex" alignItems="center">
                <ChartColumn>
                  <SmallBubbleIcon color={space.color} />
                </ChartColumn>
              </Box>
              <SpaceCategoryName
                data-testid={`${testingPrefix}_cat_name${index}`}
              >
                {space.name}
              </SpaceCategoryName>
            </Box>
          </Grid>
        </SpaceNameListRow>
      ))}
    </JustifyCenterColumn>
  );
};

const SpaceNamesList = ({
  spacesData,
  testingPrefix,
}: {
  spacesData: ISpaceChartData[];
  testingPrefix: string;
}) => {
  const [firstSpace, ...spaces] = spacesData;

  return (
    <JustifyCenterColumn>
      <SpaceNameListRow
        className={spaces?.length && spaces.length === 1 ? 'first' : ''}
      >
        <Grid item xs={12}>
          <Box display="flex">
            <Box display="flex" alignItems="center">
              <ChartColumn>
                <SmallBubbleIcon color={firstSpace.color} />
              </ChartColumn>
            </Box>
            <SpaceCategoryName data-testid={`${testingPrefix}_cat_name0`}>
              {firstSpace.name}
            </SpaceCategoryName>
          </Box>
        </Grid>
      </SpaceNameListRow>

      <SpaceNameListRow>
        <Grid item xs={12}>
          <Box display="flex">
            {spaces.map((space: ISpaceChartData, index) => (
              <React.Fragment key={`space-${space.name}-${space.category}`}>
                <Box display="flex" alignItems="center">
                  <ChartColumn>
                    <SmallBubbleIcon color={space.color} />
                  </ChartColumn>
                </Box>
                <SpaceCategoryName
                  data-testid={`${testingPrefix}_cat_name${index + 1}`}
                >
                  {space.name}
                </SpaceCategoryName>
              </React.Fragment>
            ))}
          </Box>
        </Grid>
      </SpaceNameListRow>
    </JustifyCenterColumn>
  );
};

// TODO: Create a new component for programming
const SmallDonutChartDetail: React.FunctionComponent<ISmallDonutChartDetailProps> = ({
  title,
  subtitle,
  total,
  spacesData,
  data,
  colors,
  forceDisableAllocationDialog = false,
  showPercentPill = false,
  showNamesInTable = true,
  showStackedNames = false,
  includeDisclaimer = false,
  style,
  isTotalProgramScenario,
  totalProgramWorksheets,
  spaceTypeNamesStyle,
  categoryListStyle,
  testingPrefix,
  anchorRef,
  isDetailedChart = false,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [dialogUsf, setDialogUsf] = useState<number | string>(0);
  const [dialogPercent, setDialogPercent] = useState<number | string>(0);
  const [valueType, setValueType] = useState('');
  const project = useSelector(currentProject);

  const openDialog = (
    selectedValueType: string,
    usfValue: number | string,
    usfPercentValue: number | string
  ) => {
    if (isTotalProgramScenario && !forceDisableAllocationDialog) {
      setValueType(selectedValueType);
      setDialogUsf(usfValue);
      setDialogPercent(usfPercentValue);
      setShowDialog(true);
    }
  };

  const showProgramSubtotalUsf = () => {
    openDialog(
      SPACE_TYPE_ALLOCATION.PROGRAM_SUBTOTAL_USF,
      asUsf(total),
      total ? asPercentageRounded(1) : `${NO_DATA_TO_SHOW}% `
    );
  };

  return (
    <>
      {showDialog &&
        isTotalProgramScenario &&
        !forceDisableAllocationDialog &&
        anchorRef &&
        !window.SPWExport && (
          <SpaceTypeAllocationDialog
            showDialog={showDialog}
            setShowDialog={setShowDialog}
            valueType={valueType}
            usf={dialogUsf}
            usfPercent={dialogPercent}
            totalProgramWorksheets={totalProgramWorksheets}
            showProgramSubtotalUsf={showProgramSubtotalUsf}
            openDialog={openDialog}
            spacesData={spacesData.filter(
              (spaceData) =>
                !['Circulation-US98DjCk', 'Planning Factor-US98DjCk'].includes(
                  spaceData.category
                )
            )}
            anchorRef={anchorRef}
            isDetailedChart={isDetailedChart}
          />
        )}
      <SmallDonutChartCard container style={style}>
        <ChartTitle
          style={{ height: '16px' }}
          data-testid={`${testingPrefix}_title`}
        >
          {title}
        </ChartTitle>
        <ChartSummaryPill
          className={
            isTotalProgramScenario && !forceDisableAllocationDialog
              ? 'highlight'
              : ''
          }
          onClick={showProgramSubtotalUsf}
        >
          <ChartSummaryHeaderRow>
            <Grid item xs={7}>
              <ChartSummaryHeader data-testid={`${testingPrefix}_subtitle`}>
                {subtitle}
              </ChartSummaryHeader>
            </Grid>
            <Grid item xs={5}>
              <Box display="flex">
                <ChartSummaryHeader
                  style={{ marginLeft: 'auto' }}
                  data-testid={`${testingPrefix}_total`}
                >
                  {asUsf(total)}
                </ChartSummaryHeader>
                <ChartSummaryHeader
                  className="percent right"
                  data-testid={`${testingPrefix}_total_percent`}
                >
                  {total ? asPercentageRounded(1) : `${NO_DATA_TO_SHOW}% `}
                </ChartSummaryHeader>
              </Box>
            </Grid>
          </ChartSummaryHeaderRow>
        </ChartSummaryPill>

        <Grid container>
          <ChartContainer item xs={4}>
            <Box
              flexGrow={1}
              display="flex"
              alignItems="center"
              flexDirection="column"
            >
              <SmallDonutChart data={data} colors={colors} />
            </Box>
          </ChartContainer>

          <Grid item xs={8} style={{ paddingLeft: 22 }}>
            <SpaceRows
              spacesData={spacesData}
              showPercentPill={showPercentPill}
              dialog={isTotalProgramScenario && !forceDisableAllocationDialog}
              openDialog={openDialog}
              testingPrefix={testingPrefix}
            />
            {showNamesInTable && (
              <div style={spaceTypeNamesStyle}>
                <SpaceTypeNames
                  spacesData={spacesData}
                  testingPrefix={testingPrefix}
                />
              </div>
            )}
          </Grid>
        </Grid>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: 'auto',
          }}
        >
          {!showNamesInTable && (
            <div style={categoryListStyle}>
              {showStackedNames ? (
                <StackedCategoryNames
                  spacesData={spacesData}
                  testingPrefix={testingPrefix}
                />
              ) : (
                <SpaceNamesList
                  spacesData={spacesData}
                  testingPrefix={testingPrefix}
                />
              )}
            </div>
          )}
          {includeDisclaimer && (
            <DisclaimerTextContainer>
              <span>excludes:</span>
              <ControlledLineBreaks>
                {project.type === 'programming' ? (
                  <>
                    <span>Circulation and</span>
                    <span>&nbsp;Planning Factor</span>
                  </>
                ) : (
                  <>
                    <span>Circulation</span>
                  </>
                )}
              </ControlledLineBreaks>
            </DisclaimerTextContainer>
          )}
        </div>
      </SmallDonutChartCard>
    </>
  );
};

export default SmallDonutChartDetail;
